import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import api from '../services/api';
import AdminNotificationsPanel from './AdminNotificationsPanel';
import ModalDialog from './ModalDialog';
import styles from './AdminPanel.module.css';

const normalize = (value) => (value || '').toString().toLowerCase();

const compareNullableNumber = (a, b, dir) => {
  const av = a === null || a === undefined ? Number.NEGATIVE_INFINITY : Number(a);
  const bv = b === null || b === undefined ? Number.NEGATIVE_INFINITY : Number(b);
  if (av === bv) return 0;
  return dir === 'asc' ? av - bv : bv - av;
};

const compareNullableDate = (a, b, dir) => {
  const at = a ? new Date(a).getTime() : 0;
  const bt = b ? new Date(b).getTime() : 0;
  if (at === bt) return 0;
  return dir === 'asc' ? at - bt : bt - at;
};

const toDatetimeLocalValue = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
};

const AdminPanel = ({ section = 'main' }) => {
  const { authToken } = useContext(AuthContext);
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [overview, setOverview] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [replacementEvents, setReplacementEvents] = useState([]);
  const [replacementDeviceId, setReplacementDeviceId] = useState('');
  const [replacementHour, setReplacementHour] = useState('');
  const [replacementNote, setReplacementNote] = useState('');

  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [newAssignment, setNewAssignment] = useState({ user_id: '', device_id: '', custom_name: '' });
  const [newKiosk, setNewKiosk] = useState({ name: '', slug: '', device_ids: [], device_names: {} });
  const [activationLink, setActivationLink] = useState('');

  const [kioskSearch, setKioskSearch] = useState('');
  const [kioskEdit, setKioskEdit] = useState(null);

  const [userSearch, setUserSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [assignmentSearch, setAssignmentSearch] = useState('');

  const [overviewSearch, setOverviewSearch] = useState('');
  const [minHepaUsage, setMinHepaUsage] = useState('');
  const [maxHepaUsage, setMaxHepaUsage] = useState('');
  const [overviewSort, setOverviewSort] = useState({ key: 'last_connection', dir: 'desc' });
  const [passwordModal, setPasswordModal] = useState(null);
  const [adminMessageModal, setAdminMessageModal] = useState(null);
  const [replacementEditModal, setReplacementEditModal] = useState(null);
  const [assignmentEditModal, setAssignmentEditModal] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(null);
  const [deviceMetaModal, setDeviceMetaModal] = useState(null);
  const [createUserModal, setCreateUserModal] = useState(null);
  const [createAssignmentModal, setCreateAssignmentModal] = useState(null);

  useEffect(() => {
    if (!authToken) {
      navigate('/login');
    }
  }, [authToken, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [
        meRes,
        usersRes,
        devicesRes,
        assignmentsRes,
        overviewRes,
        kiosksRes,
      ] = await Promise.all([
        api.get('/me/'),
        api.get('/admin/users/'),
        api.get('/devices/'),
        api.get('/admin/assignments/'),
        api.get('/admin/filters/overview/'),
        api.get('/admin/kiosks/'),
      ]);

      setMe(meRes.data);
      setUsers(usersRes.data);
      setDevices(devicesRes.data);
      setAssignments(assignmentsRes.data);
      setOverview(overviewRes.data);
      setKiosks(kiosksRes.data);
      if (devicesRes.data.length > 0) {
        setReplacementDeviceId((prev) => (prev ? prev : String(devicesRes.data[0].id)));
      } else {
        setReplacementDeviceId('');
      }
    } catch (error) {
      console.error('Admin panel load error:', error);
      alert(t('adminOnly'));
      navigate('/dashboard');
    }
  }, [navigate, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadReplacementEvents = useCallback(async (deviceId) => {
    if (!deviceId) {
      setReplacementEvents([]);
      return;
    }
    try {
      const response = await api.get('/admin/replacements/', { params: { device_id: deviceId } });
      setReplacementEvents(response.data || []);
    } catch (error) {
      console.error('Load replacement events error:', error);
      setReplacementEvents([]);
    }
  }, []);

  useEffect(() => {
    loadReplacementEvents(replacementDeviceId);
  }, [replacementDeviceId, loadReplacementEvents]);

  const visibleUsers = useMemo(() => {
    const q = normalize(userSearch);
    return users.filter((user) => normalize(user.username).includes(q));
  }, [users, userSearch]);

  const visibleDevices = useMemo(() => {
    const q = normalize(deviceSearch);
    return devices.filter((device) => {
      const haystack = `${device.name || ''} ${device.mac || ''}`;
      return normalize(haystack).includes(q);
    });
  }, [devices, deviceSearch]);

  const visibleAssignments = useMemo(() => {
    const q = normalize(assignmentSearch);
    return assignments.filter((assignment) => {
      const haystack = `${assignment.username || ''} ${assignment.mac || ''} ${assignment.custom_name || ''} ${assignment.device_name || ''}`;
      return normalize(haystack).includes(q);
    });
  }, [assignments, assignmentSearch]);

  const visibleOverview = useMemo(() => {
    const min = minHepaUsage === '' ? null : Number(minHepaUsage);
    const max = maxHepaUsage === '' ? null : Number(maxHepaUsage);
    const search = normalize(overviewSearch);

    const filtered = overview.filter((row) => {
      const haystack = `${row.system_name || ''} ${row.mac || ''}`;
      const textMatch = normalize(haystack).includes(search);
      if (!textMatch) {
        return false;
      }

      const usage = row.hepa_usage;
      if (min !== null && !Number.isNaN(min) && (usage === null || Number(usage) < min)) {
        return false;
      }
      if (max !== null && !Number.isNaN(max) && (usage === null || Number(usage) > max)) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (overviewSort.key === 'system_name') {
        const cmp = normalize(a.system_name || a.mac).localeCompare(normalize(b.system_name || b.mac));
        return overviewSort.dir === 'asc' ? cmp : -cmp;
      }
      if (overviewSort.key === 'mac') {
        const cmp = normalize(a.mac).localeCompare(normalize(b.mac));
        return overviewSort.dir === 'asc' ? cmp : -cmp;
      }
      if (overviewSort.key === 'hepa_usage') {
        return compareNullableNumber(a.hepa_usage, b.hepa_usage, overviewSort.dir);
      }
      if (overviewSort.key === 'last_connection') {
        return compareNullableDate(a.last_connection, b.last_connection, overviewSort.dir);
      }
      if (overviewSort.key === 'assignment_count') {
        return compareNullableNumber(a.assignment_count, b.assignment_count, overviewSort.dir);
      }
      return 0;
    });

    return sorted;
  }, [overview, overviewSearch, minHepaUsage, maxHepaUsage, overviewSort]);

  const visibleKiosks = useMemo(() => {
    const q = normalize(kioskSearch);
    return kiosks.filter((kiosk) => normalize(`${kiosk.name} ${kiosk.slug}`).includes(q));
  }, [kiosks, kioskSearch]);

  const toggleOverviewSort = (key) => {
    setOverviewSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  };

  const sortIndicator = (key) => {
    if (overviewSort.key !== key) {
      return '↕';
    }
    return overviewSort.dir === 'asc' ? '↑' : '↓';
  };

  const createUser = async (e) => {
    e?.preventDefault();
    if (!newUser.username.trim() || !newUser.password) {
      setCreateUserModal((prev) => (prev ? { ...prev, error: t('saveError') } : prev));
      return;
    }
    try {
      await api.post('/admin/users/', {
        username: newUser.username.trim(),
        password: newUser.password,
        is_staff: false,
        is_superuser: false,
        is_active: true,
      });
      setNewUser({ username: '', password: '' });
      setCreateUserModal(null);
      await loadAll();
    } catch (error) {
      console.error('Create user error:', error);
      setCreateUserModal((prev) => (prev ? { ...prev, error: t('saveError') } : prev));
    }
  };

  const createAssignment = async (e) => {
    e?.preventDefault();
    if (!newAssignment.user_id || !newAssignment.device_id) {
      setCreateAssignmentModal((prev) => (prev ? { ...prev, error: t('saveError') } : prev));
      return;
    }

    try {
      await api.post('/admin/assignments/', {
        user_id: Number(newAssignment.user_id),
        device_id: Number(newAssignment.device_id),
        custom_name: newAssignment.custom_name,
      });
      setNewAssignment({ user_id: '', device_id: '', custom_name: '' });
      setCreateAssignmentModal(null);
      await loadAll();
    } catch (error) {
      console.error('Create assignment error:', error);
      setCreateAssignmentModal((prev) => (prev ? { ...prev, error: t('saveError') } : prev));
    }
  };

  const toggleKioskDevice = (stateSetter, deviceId) => {
    stateSetter((prev) => {
      const has = prev.device_ids.includes(deviceId);
      const deviceIds = has ? prev.device_ids.filter((id) => id !== deviceId) : [...prev.device_ids, deviceId];
      const deviceNames = { ...(prev.device_names || {}) };
      if (has) {
        delete deviceNames[String(deviceId)];
      }
      return { ...prev, device_ids: deviceIds, device_names: deviceNames };
    });
  };

  const changeKioskDeviceName = (stateSetter, deviceId, value) => {
    stateSetter((prev) => ({
      ...prev,
      device_names: {
        ...(prev.device_names || {}),
        [String(deviceId)]: value,
      },
    }));
  };

  const createKiosk = async (e) => {
    e.preventDefault();
    if (!newKiosk.name.trim() || !newKiosk.slug.trim()) {
      return;
    }

    const names = {};
    newKiosk.device_ids.forEach((id) => {
      const name = (newKiosk.device_names[String(id)] || '').trim();
      if (name) {
        names[String(id)] = name;
      }
    });

    try {
      await api.post('/admin/kiosks/', {
        name: newKiosk.name.trim(),
        slug: newKiosk.slug.trim(),
        is_active: true,
        device_ids: newKiosk.device_ids,
        device_names: names,
      });
      setNewKiosk({ name: '', slug: '', device_ids: [], device_names: {} });
      await loadAll();
    } catch (error) {
      console.error('Create kiosk error:', error);
      alert(t('saveError'));
    }
  };

  const startEditKiosk = (kiosk) => {
    setKioskEdit({
      id: kiosk.id,
      name: kiosk.name || '',
      slug: kiosk.slug || '',
      allowed_ip: kiosk.allowed_ip || '',
      is_active: Boolean(kiosk.is_active),
      device_ids: [...(kiosk.device_ids || [])],
      device_names: { ...(kiosk.device_names || {}) },
    });
  };

  const saveKioskEdit = async (e) => {
    e.preventDefault();
    if (!kioskEdit || !kioskEdit.name.trim() || !kioskEdit.slug.trim()) {
      return;
    }

    const names = {};
    kioskEdit.device_ids.forEach((id) => {
      const name = (kioskEdit.device_names[String(id)] || '').trim();
      if (name) {
        names[String(id)] = name;
      }
    });

    try {
      await api.patch(`/admin/kiosks/${kioskEdit.id}/`, {
        name: kioskEdit.name.trim(),
        slug: kioskEdit.slug.trim(),
        allowed_ip: kioskEdit.allowed_ip,
        is_active: kioskEdit.is_active,
        device_ids: kioskEdit.device_ids,
        device_names: names,
      });
      await loadAll();
      setKioskEdit(null);
    } catch (error) {
      console.error('Edit kiosk error:', error);
      alert(t('saveError'));
    }
  };

  const toggleKioskActive = async (kiosk) => {
    try {
      await api.patch(`/admin/kiosks/${kiosk.id}/`, { is_active: !kiosk.is_active });
      await loadAll();
    } catch (error) {
      console.error('Toggle kiosk state error:', error);
      alert(t('saveError'));
    }
  };

  const generateActivation = async (kioskId) => {
    try {
      const response = await api.post(`/admin/kiosks/${kioskId}/activation/`);
      setActivationLink(response.data.activation_url);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(response.data.activation_url);
      }
      alert(t('activationLinkGenerated'));
    } catch (error) {
      console.error('Generate activation link error:', error);
      alert(t('saveError'));
    }
  };

  const deleteKiosk = async (kioskId) => {
    if (!window.confirm(t('deleteConfirm'))) {
      return;
    }
    try {
      await api.delete(`/admin/kiosks/${kioskId}/`);
      await loadAll();
      if (kioskEdit?.id === kioskId) {
        setKioskEdit(null);
      }
    } catch (error) {
      console.error('Delete kiosk error:', error);
      alert(t('saveError'));
    }
  };

  const renameAssignment = async (assignment) => {
    setAssignmentEditModal({
      id: assignment.id,
      username: assignment.username,
      mac: assignment.mac,
      name: assignment.custom_name || assignment.device_name || '',
      error: '',
    });
  };

  const deleteAssignment = async (assignmentId) => {
    const assignment = assignments.find((item) => item.id === assignmentId);
    if (!assignment) {
      return;
    }
    setConfirmDeleteModal({
      title: t('userAssignments'),
      message: t('deleteConfirm'),
      onConfirm: async () => {
        try {
          await api.delete(`/admin/assignments/${assignmentId}/`);
          await loadAll();
        } catch (error) {
          console.error('Delete assignment error:', error);
          setAdminMessageModal({ title: t('userAssignments'), message: t('saveError') });
        } finally {
          setConfirmDeleteModal(null);
        }
      },
    });
  };

  const changeUserPassword = async (user) => {
    setPasswordModal({
      user,
      password: '',
      confirmPassword: '',
      error: '',
    });
  };

  const submitUserPasswordChange = async () => {
    if (!passwordModal) {
      return;
    }
    if (!passwordModal.password || !passwordModal.confirmPassword) {
      setPasswordModal((prev) => ({ ...prev, error: t('passwordRequired') }));
      return;
    }
    if (passwordModal.password !== passwordModal.confirmPassword) {
      setPasswordModal((prev) => ({ ...prev, error: t('passwordMismatch') }));
      return;
    }

    try {
      await api.patch(`/admin/users/${passwordModal.user.id}/password/`, { password: passwordModal.password });
      setPasswordModal(null);
      setAdminMessageModal({ title: t('changePassword'), message: t('passwordChanged') });
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  const toggleUserActive = async (user) => {
    setConfirmDeleteModal({
      title: user.is_active ? t('deactivateUser') : t('activateUser'),
      message: user.is_active ? t('deactivateUserConfirm') : t('activateUserConfirm'),
      confirmLabel: user.is_active ? t('deactivateUser') : t('activateUser'),
      onConfirm: async () => {
        try {
          await api.patch(`/admin/users/${user.id}/`, { is_active: !user.is_active });
          await loadAll();
        } catch (error) {
          console.error('Toggle user active error:', error);
          setAdminMessageModal({ title: t('usersList'), message: error?.response?.data?.error || t('saveError') });
        } finally {
          setConfirmDeleteModal(null);
        }
      },
    });
  };

  const editDeviceMeta = async (device) => {
    setDeviceMetaModal({
      id: device.id,
      system_name: device.system_name || device.name || device.mac,
      serial_number: device.serial_number || '',
      location: device.location || '',
      error: '',
    });
  };

  const submitAssignmentEdit = async () => {
    if (!assignmentEditModal) {
      return;
    }
    const nextName = (assignmentEditModal.name || '').trim();
    if (!nextName) {
      setAssignmentEditModal((prev) => ({ ...prev, error: t('filterNameRequired') }));
      return;
    }
    try {
      await api.patch(`/admin/assignments/${assignmentEditModal.id}/`, { name: nextName });
      setAssignmentEditModal(null);
      await loadAll();
    } catch (error) {
      console.error('Rename assignment error:', error);
      setAssignmentEditModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  const submitDeviceMetaEdit = async () => {
    if (!deviceMetaModal) {
      return;
    }
    try {
      await api.patch(`/admin/devices/${deviceMetaModal.id}/`, {
        system_name: deviceMetaModal.system_name,
        serial_number: deviceMetaModal.serial_number,
        location: deviceMetaModal.location,
      });
      setDeviceMetaModal(null);
      await loadAll();
    } catch (error) {
      console.error('Edit device meta error:', error);
      setDeviceMetaModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  const createReplacementEvent = async (e) => {
    e.preventDefault();
    if (!replacementDeviceId || !replacementHour) {
      return;
    }
    try {
      await api.post('/admin/replacements/', {
        device_id: Number(replacementDeviceId),
        replacement_hour: replacementHour,
        source: 'manual',
        note: replacementNote,
      });
      setReplacementNote('');
      await loadReplacementEvents(replacementDeviceId);
    } catch (error) {
      console.error('Create replacement event error:', error);
      alert(t('saveError'));
    }
  };

  const editReplacementEvent = async (event) => {
    setReplacementEditModal({
      id: event.id,
      replacement_hour: toDatetimeLocalValue(event.replacement_hour),
      note: event.note || '',
      error: '',
    });
  };

  const submitReplacementEventEdit = async () => {
    if (!replacementEditModal) {
      return;
    }
    if (!replacementEditModal.replacement_hour) {
      setReplacementEditModal((prev) => ({ ...prev, error: t('replacementHourRequired') }));
      return;
    }
    try {
      await api.patch(`/admin/replacements/${replacementEditModal.id}/`, {
        replacement_hour: replacementEditModal.replacement_hour,
        note: replacementEditModal.note,
      });
      setReplacementEditModal(null);
      await loadReplacementEvents(replacementDeviceId);
    } catch (error) {
      console.error('Edit replacement event error:', error);
      setReplacementEditModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  const deleteReplacementEvent = async (eventId) => {
    if (!window.confirm(t('deleteConfirm'))) {
      return;
    }
    try {
      await api.delete(`/admin/replacements/${eventId}/`);
      await loadReplacementEvents(replacementDeviceId);
    } catch (error) {
      console.error('Delete replacement event error:', error);
      alert(t('saveError'));
    }
  };

  if (!me) {
    return <div className={styles.loading}>{t('loading')}</div>;
  }

  return (
    <div className={styles.page}>
      {replacementEditModal && (
        <ModalDialog
          title={t('editReplacementEvent')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitReplacementEventEdit}
          onClose={() => setReplacementEditModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('replacementHour')}</label>
              <input
                type="datetime-local"
                value={replacementEditModal.replacement_hour}
                onChange={(e) => setReplacementEditModal((prev) => ({
                  ...prev,
                  replacement_hour: e.target.value,
                  error: '',
                }))}
                placeholder={t('replacementHour')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('replacementNote')}</label>
              <input
                type="text"
                value={replacementEditModal.note}
                onChange={(e) => setReplacementEditModal((prev) => ({
                  ...prev,
                  note: e.target.value,
                  error: '',
                }))}
                placeholder={t('replacementNote')}
              />
            </div>
            {replacementEditModal.error && <p className={styles.modalError}>{replacementEditModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {assignmentEditModal && (
        <ModalDialog
          title={t('rename')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitAssignmentEdit}
          onClose={() => setAssignmentEditModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('customFilterName')}</label>
              <input
                type="text"
                value={assignmentEditModal.name}
                onChange={(e) => setAssignmentEditModal((prev) => ({ ...prev, name: e.target.value, error: '' }))}
                placeholder={t('renameFilterPrompt')}
              />
            </div>
            {assignmentEditModal.error && <p className={styles.modalError}>{assignmentEditModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {deviceMetaModal && (
        <ModalDialog
          title={t('devicesAdmin')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitDeviceMetaEdit}
          onClose={() => setDeviceMetaModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('devices')}</label>
              <input
                type="text"
                value={deviceMetaModal.system_name}
                onChange={(e) => setDeviceMetaModal((prev) => ({ ...prev, system_name: e.target.value, error: '' }))}
                placeholder={t('globalFilterNamePrompt')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('serialNumber')}</label>
              <input
                type="text"
                value={deviceMetaModal.serial_number}
                onChange={(e) => setDeviceMetaModal((prev) => ({ ...prev, serial_number: e.target.value, error: '' }))}
                placeholder={t('serialNumberPrompt')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('location')}</label>
              <input
                type="text"
                value={deviceMetaModal.location}
                onChange={(e) => setDeviceMetaModal((prev) => ({ ...prev, location: e.target.value, error: '' }))}
                placeholder={t('locationPrompt')}
              />
            </div>
            {deviceMetaModal.error && <p className={styles.modalError}>{deviceMetaModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {createUserModal && (
        <ModalDialog
          title={t('createUser')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={createUser}
          onClose={() => {
            setCreateUserModal(null);
            setNewUser({ username: '', password: '' });
          }}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('username')}</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => {
                  setNewUser((prev) => ({ ...prev, username: e.target.value }));
                  setCreateUserModal((prev) => (prev ? { ...prev, error: '' } : prev));
                }}
                placeholder={t('username')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('password')}</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => {
                  setNewUser((prev) => ({ ...prev, password: e.target.value }));
                  setCreateUserModal((prev) => (prev ? { ...prev, error: '' } : prev));
                }}
                placeholder={t('password')}
              />
            </div>
            {createUserModal.error && <p className={styles.modalError}>{createUserModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {createAssignmentModal && (
        <ModalDialog
          title={t('assignFilter')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={createAssignment}
          onClose={() => {
            setCreateAssignmentModal(null);
            setNewAssignment({ user_id: '', device_id: '', custom_name: '' });
          }}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('selectUser')}</label>
              <select
                value={newAssignment.user_id}
                onChange={(e) => {
                  setNewAssignment((prev) => ({ ...prev, user_id: e.target.value }));
                  setCreateAssignmentModal((prev) => (prev ? { ...prev, error: '' } : prev));
                }}
              >
                <option value="">{t('selectUser')}</option>
                {visibleUsers.map((user) => (
                  <option key={user.id} value={user.id}>{user.username}</option>
                ))}
              </select>
            </div>
            <div className={styles.modalField}>
              <label>{t('selectFilter')}</label>
              <select
                value={newAssignment.device_id}
                onChange={(e) => {
                  setNewAssignment((prev) => ({ ...prev, device_id: e.target.value }));
                  setCreateAssignmentModal((prev) => (prev ? { ...prev, error: '' } : prev));
                }}
              >
                <option value="">{t('selectFilter')}</option>
                {visibleDevices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name} ({device.mac})
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.modalField}>
              <label>{t('customFilterName')}</label>
              <input
                type="text"
                value={newAssignment.custom_name}
                onChange={(e) => {
                  setNewAssignment((prev) => ({ ...prev, custom_name: e.target.value }));
                  setCreateAssignmentModal((prev) => (prev ? { ...prev, error: '' } : prev));
                }}
                placeholder={t('customFilterName')}
              />
            </div>
            {createAssignmentModal.error && <p className={styles.modalError}>{createAssignmentModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {confirmDeleteModal && (
        <ModalDialog
          title={confirmDeleteModal.title}
          message={confirmDeleteModal.message}
          confirmLabel={confirmDeleteModal.confirmLabel || t('delete')}
          cancelLabel={t('cancel')}
          onConfirm={confirmDeleteModal.onConfirm}
          onClose={() => setConfirmDeleteModal(null)}
        />
      )}
      {passwordModal && (
        <ModalDialog
          title={`${t('changePassword')} (${passwordModal.user.username})`}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitUserPasswordChange}
          onClose={() => setPasswordModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('newPasswordPrompt')}</label>
              <input
                type="password"
                value={passwordModal.password}
                onChange={(e) => setPasswordModal((prev) => ({ ...prev, password: e.target.value, error: '' }))}
                placeholder={t('newPasswordPrompt')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('confirmPasswordPrompt')}</label>
              <input
                type="password"
                value={passwordModal.confirmPassword}
                onChange={(e) => setPasswordModal((prev) => ({ ...prev, confirmPassword: e.target.value, error: '' }))}
                placeholder={t('confirmPasswordPrompt')}
              />
            </div>
            {passwordModal.error && <p className={styles.modalError}>{passwordModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {adminMessageModal && (
        <ModalDialog
          title={adminMessageModal.title}
          message={adminMessageModal.message}
          confirmLabel={t('close')}
          cancelLabel={t('close')}
          hideConfirm
          onConfirm={() => setAdminMessageModal(null)}
          onClose={() => setAdminMessageModal(null)}
        />
      )}
      <header className={styles.hero}>
        <div>
          <p className={styles.badge}>{t('adminPanel')}</p>
          <h1 className={styles.title}>{t('adminPanel')}</h1>
          <p className={styles.subtitle}>IFMO Control Center</p>
        </div>
        <div className={styles.topActions}>
          <button
            className={`${styles.secondaryButton} ${section === 'users' ? styles.activeTab : ''}`}
            onClick={() => navigate('/admin/users')}
          >
            {t('adminUsersTab')}
          </button>
          <button
            className={`${styles.secondaryButton} ${section === 'filters' ? styles.activeTab : ''}`}
            onClick={() => navigate('/admin/filters')}
          >
            {t('adminFiltersTab')}
          </button>
          <button
            className={`${styles.secondaryButton} ${section === 'kiosk' ? styles.activeTab : ''}`}
            onClick={() => navigate('/admin/kiosks')}
          >
            {t('adminKioskTab')}
          </button>
          <button className={styles.secondaryButton} onClick={() => navigate('/dashboard')}>
            {t('dashboard')}
          </button>
        </div>
      </header>

      {section === 'kiosk' && (
        <>
          <section className={styles.card}>
            <h2>{t('kioskManagement')}</h2>
            <form onSubmit={createKiosk} className={styles.inlineForm}>
              <input
                value={newKiosk.name}
                onChange={(e) => setNewKiosk({ ...newKiosk, name: e.target.value })}
                placeholder={t('kioskName')}
                required
              />
              <input
                value={newKiosk.slug}
                onChange={(e) => setNewKiosk({ ...newKiosk, slug: e.target.value })}
                placeholder={t('kioskSlug')}
                required
              />
              <button className={styles.primaryButton} type="submit">{t('createKiosk')}</button>
            </form>

            <div className={styles.checkboxWrap}>
              {visibleDevices.map((device) => {
                const checked = newKiosk.device_ids.includes(device.id);
                return (
                  <div key={device.id} className={styles.deviceConfigItem}>
                    <label className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleKioskDevice(setNewKiosk, device.id)}
                      />
                      <span>{device.name} ({device.mac})</span>
                    </label>
                    {checked && (
                      <input
                        value={newKiosk.device_names[String(device.id)] || ''}
                        onChange={(e) => changeKioskDeviceName(setNewKiosk, device.id, e.target.value)}
                        placeholder={t('kioskFilterName')}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {activationLink && (
              <div className={styles.activationBox}>
                <strong>{t('latestActivationLink')}</strong>
                <div>{activationLink}</div>
              </div>
            )}
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <h2>{t('kiosksList')}</h2>
              <input
                value={kioskSearch}
                onChange={(e) => setKioskSearch(e.target.value)}
                placeholder={t('searchKiosks')}
              />
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t('kioskName')}</th>
                    <th>{t('kioskSlug')}</th>
                    <th>{t('devices')}</th>
                    <th>{t('status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleKiosks.map((kiosk) => (
                    <tr key={kiosk.id}>
                      <td>{kiosk.id}</td>
                      <td>{kiosk.name}</td>
                      <td>{kiosk.slug}</td>
                      <td>{kiosk.device_count}</td>
                      <td>{kiosk.is_active ? t('activeStatus') : t('inactiveStatus')}</td>
                      <td className={styles.rowActions}>
                        <button className={styles.secondaryButton} onClick={() => startEditKiosk(kiosk)}>
                          {t('edit')}
                        </button>
                        <button className={styles.secondaryButton} onClick={() => toggleKioskActive(kiosk)}>
                          {kiosk.is_active ? t('deactivateKiosk') : t('activateKiosk')}
                        </button>
                        <button className={styles.secondaryButton} onClick={() => generateActivation(kiosk.id)}>
                          {t('generateActivationLink')}
                        </button>
                        <button className={styles.ghostButton} onClick={() => deleteKiosk(kiosk.id)}>
                          {t('delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleKiosks.length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {kioskEdit && (
            <section className={styles.card}>
              <h2>{t('editKiosk')}</h2>
              <form onSubmit={saveKioskEdit} className={styles.inlineForm}>
                <input
                  value={kioskEdit.name}
                  onChange={(e) => setKioskEdit({ ...kioskEdit, name: e.target.value })}
                  placeholder={t('kioskName')}
                  required
                />
                <input
                  value={kioskEdit.slug}
                  onChange={(e) => setKioskEdit({ ...kioskEdit, slug: e.target.value })}
                  placeholder={t('kioskSlug')}
                  required
                />
                <input
                  value={kioskEdit.allowed_ip}
                  onChange={(e) => setKioskEdit({ ...kioskEdit, allowed_ip: e.target.value })}
                  placeholder={t('allowedIp')}
                />
                <label className={styles.inlineCheck}>
                  <input
                    type="checkbox"
                    checked={kioskEdit.is_active}
                    onChange={(e) => setKioskEdit({ ...kioskEdit, is_active: e.target.checked })}
                  />
                  <span>{t('activeStatus')}</span>
                </label>
                <button className={styles.primaryButton} type="submit">{t('save')}</button>
                <button className={styles.ghostButton} type="button" onClick={() => setKioskEdit(null)}>{t('cancel')}</button>
              </form>

              <div className={styles.checkboxWrap}>
                {visibleDevices.map((device) => {
                  const checked = kioskEdit.device_ids.includes(device.id);
                  return (
                    <div key={device.id} className={styles.deviceConfigItem}>
                      <label className={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleKioskDevice(setKioskEdit, device.id)}
                        />
                        <span>{device.name} ({device.mac})</span>
                      </label>
                      {checked && (
                        <input
                          value={kioskEdit.device_names[String(device.id)] || ''}
                          onChange={(e) => changeKioskDeviceName(setKioskEdit, device.id, e.target.value)}
                          placeholder={t('kioskFilterName')}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {section === 'users' && (
        <>
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <h2>{t('usersList')}</h2>
              <div className={styles.rowActions}>
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder={t('searchUsers')}
                />
                <button
                  className={styles.primaryButton}
                  onClick={() => {
                    setNewUser({ username: '', password: '' });
                    setCreateUserModal({ error: '' });
                  }}
                >
                  {t('createUser')}
                </button>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('username')}</th>
                    <th>{t('role')}</th>
                    <th>{t('status')}</th>
                    <th>{t('lastLogin')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.is_superuser ? t('adminRole') : t('userRole')}</td>
                      <td>{user.is_active ? t('activeStatus') : t('inactiveStatus')}</td>
                      <td>{user.last_login ? new Date(user.last_login).toLocaleString() : '-'}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.secondaryButton} onClick={() => changeUserPassword(user)}>
                            {t('changePassword')}
                          </button>
                          <button className={styles.ghostButton} onClick={() => toggleUserActive(user)}>
                            {user.is_active ? t('deactivateUser') : t('activateUser')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <h2>{t('userAssignments')}</h2>
              <div className={styles.rowActions}>
                <input
                  value={assignmentSearch}
                  onChange={(e) => setAssignmentSearch(e.target.value)}
                  placeholder={t('searchAssignments')}
                />
                <input
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder={t('searchDevices')}
                />
                <button
                  className={styles.primaryButton}
                  onClick={() => {
                    setNewAssignment({ user_id: '', device_id: '', custom_name: '' });
                    setCreateAssignmentModal({ error: '' });
                  }}
                >
                  {t('assignFilter')}
                </button>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('username')}</th>
                    <th>{t('devices')}</th>
                    <th>{t('customFilterName')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAssignments.map((assignment) => (
                    <tr key={assignment.id}>
                      <td>{assignment.username}</td>
                      <td>{assignment.mac}</td>
                      <td>{assignment.custom_name || assignment.device_name}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.secondaryButton} onClick={() => renameAssignment(assignment)}>
                            {t('rename')}
                          </button>
                          <button className={styles.ghostButton} onClick={() => deleteAssignment(assignment.id)}>
                            {t('delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleAssignments.length === 0 && (
                    <tr>
                      <td colSpan={4} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <AdminNotificationsPanel />
        </>
      )}

      {section === 'filters' && (
        <>
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <h2>{t('devicesAdmin')}</h2>
              <input
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                placeholder={t('searchDevices')}
              />
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('devices')}</th>
                    <th>MAC</th>
                    <th>{t('serialNumber')}</th>
                    <th>{t('location')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDevices.map((device) => (
                    <tr key={device.id}>
                      <td>{device.system_name || device.name || device.mac}</td>
                      <td>{device.mac}</td>
                      <td>{device.serial_number || '-'}</td>
                      <td>{device.location || '-'}</td>
                      <td>
                        <button className={styles.secondaryButton} onClick={() => editDeviceMeta(device)}>
                          {t('edit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleDevices.length === 0 && (
                    <tr>
                      <td colSpan={5} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.card}>
            <h2>{t('hepaEvents')}</h2>
            <form onSubmit={createReplacementEvent} className={styles.inlineForm}>
              <select
                value={replacementDeviceId}
                onChange={(e) => setReplacementDeviceId(e.target.value)}
                required
              >
                <option value="">{t('selectFilter')}</option>
                {visibleDevices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {(device.system_name || device.name || device.mac)} ({device.mac})
                  </option>
                ))}
              </select>
              <input
                type="datetime-local"
                value={replacementHour}
                onChange={(e) => setReplacementHour(e.target.value)}
                required
              />
              <input
                value={replacementNote}
                onChange={(e) => setReplacementNote(e.target.value)}
                placeholder={t('replacementNote')}
              />
              <button className={styles.primaryButton} type="submit">{t('addReplacementEvent')}</button>
            </form>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('replacementHour')}</th>
                    <th>{t('source')}</th>
                    <th>{t('replacementNote')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {replacementEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{new Date(event.replacement_hour).toLocaleString()}</td>
                      <td>{event.source}</td>
                      <td>{event.note || '-'}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.secondaryButton} onClick={() => editReplacementEvent(event)}>
                            {t('edit')}
                          </button>
                          <button className={styles.ghostButton} onClick={() => deleteReplacementEvent(event.id)}>
                            {t('delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {replacementEvents.length === 0 && (
                    <tr>
                      <td colSpan={4} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeaderMulti}>
              <h2>{t('filtersOverview')}</h2>
              <div className={styles.filterRow}>
                <input
                  value={overviewSearch}
                  onChange={(e) => setOverviewSearch(e.target.value)}
                  placeholder={t('searchFilters')}
                />
                <input
                  type="number"
                  step="0.1"
                  value={minHepaUsage}
                  onChange={(e) => setMinHepaUsage(e.target.value)}
                  placeholder={t('minHepa')}
                />
                <input
                  type="number"
                  step="0.1"
                  value={maxHepaUsage}
                  onChange={(e) => setMaxHepaUsage(e.target.value)}
                  placeholder={t('maxHepa')}
                />
                <button
                  className={styles.ghostButton}
                  onClick={() => {
                    setOverviewSearch('');
                    setMinHepaUsage('');
                    setMaxHepaUsage('');
                  }}
                >
                  {t('clearFilters')}
                </button>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.sortable} onClick={() => toggleOverviewSort('system_name')}>
                      {t('devices')} <span>{sortIndicator('system_name')}</span>
                    </th>
                    <th className={styles.sortable} onClick={() => toggleOverviewSort('mac')}>
                      MAC <span>{sortIndicator('mac')}</span>
                    </th>
                    <th>{t('serialNumber')}</th>
                    <th>{t('location')}</th>
                    <th className={styles.sortable} onClick={() => toggleOverviewSort('hepa_usage')}>
                      {t('hepaUsagePercent')} <span>{sortIndicator('hepa_usage')}</span>
                    </th>
                    <th className={styles.sortable} onClick={() => toggleOverviewSort('last_connection')}>
                      {t('last')} <span>{sortIndicator('last_connection')}</span>
                    </th>
                    <th className={styles.sortable} onClick={() => toggleOverviewSort('assignment_count')}>
                      {t('assignedUsers')} <span>{sortIndicator('assignment_count')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOverview.map((row) => (
                    <tr key={row.id}>
                      <td>{row.system_name || row.mac}</td>
                      <td>{row.mac}</td>
                      <td>{row.serial_number || '-'}</td>
                      <td>{row.location || '-'}</td>
                      <td>{row.hepa_usage === null ? '-' : `${Number(row.hepa_usage).toFixed(1)}%`}</td>
                      <td>{row.last_connection ? new Date(row.last_connection).toLocaleString() : '-'}</td>
                      <td>{row.assignment_count}</td>
                    </tr>
                  ))}
                  {visibleOverview.length === 0 && (
                    <tr>
                      <td colSpan={7} className={styles.emptyRow}>{t('noResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminPanel;
